import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import React, { useEffect, useState } from 'react';
import Cta from '../../components/buttons/Cta';
import TicketDetailHeader from '../../components/headers/TicketdetailHeader';
import InputField from '../../components/inputs/text/InputField';
import AssetHeader from '../../components/products/AssetHeader';
import SelectAssetTable from '../../components/tables/SelectAssetTable';
import TicketTag from '../../components/tags/TicketTag';
import {
  Ticket,
  TicketDetailProps,
  TicketHistoryItem,
  TicketTimeEnty,
} from '../../interfaces/Ticket';
import { AssetsService } from '../../services/assets/Assets';
import { TicketService } from '../../services/tickets/Tickets';
import { differenceInMs } from '../../utils/dates/DifferenceInMs';
import TicketTimeEntries from './TicketTimeEntries';

dayjs.extend(duration);

const BoTicketDetailContainer: React.FC<TicketDetailProps> = ({ ticket }) => {
  const [totalRecordedTimeRounded, setTotalRecordedTimeRounded] = useState<
    string | null
  >(null);
  const [ticketHistory, setTicketHistory] = useState<TicketHistoryItem[]>([]);
  const [time, setTime] = useState(0);
  const [roundedStoppedTime, setRoundedStoppedTime] = useState(0);
  const [ticketDate, setTicketDate] = useState(0);
  const [timeEntries, setTimeEntries] = useState([]);
  const [assetSearch, setAssetSearch] = useState('');
  const [assetLoading, setAssetLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [asset, setAsset] = useState([]);
  const [assetTicketHistory, setAssetTicketHistory] = useState();
  const [toggleAssetSearch, setToggleAssetSearch] = useState(false);
  const ticketService = new TicketService();
  const assetService = new AssetsService();
  const addHistoryEvent = (
    statusType: TicketHistoryItem['status'],
    details?: string
  ) => {
    setTicketHistory((prevHistory) => [
      ...prevHistory,
      {
        status: statusType,
        created_at: new Date().toISOString(),
        event: details,
      },
    ]);

    const eventData = {
      ticket_id: ticket.tic_id,
      emp_id: ticket.assignedEmployee.med_id,
      status: statusType,
      event: details,
    };

    ticketService.addTicketEvent(ticket.tic_id, eventData);
  };

  const fetchHistory = async () => {
    const historyResponse = await ticketService.getTicketEvents(ticket.tic_id);
    if (historyResponse && historyResponse.logs) {
      setTicketHistory(historyResponse.logs);
    }
    const entries = await ticketService.getTicketTimeEntries(ticket.tic_id);
    setTimeEntries(entries.entries);
    setRoundedStoppedTime(totalTime());
  };

  const totalTime = () => {
    const totalTimeMs = timeEntries.reduce(
      (total, entry) => total + (entry.total_time_rounded / 60) * 60,
      0
    );

    return dayjs.duration(totalTimeMs);
  };

  const addTimeEntry = async (id: number, entry: TicketTimeEnty) => {
    await ticketService.addTicketTimeEntry(id, entry);
  };

  const checkEntries = () => {
    if (!ticket.timeEntries.start_timestamp) return;
    if (ticket.timeEntries.end_timestamp) return;
    setTime(differenceInMs(ticket?.timeEntries.start_timestamp));
  };

  const fetchAssetHistory = async () => {
    if (!ticket.tic_pro_id) {
      return;
    }
    const assetHistory = await ticketService.getAssetTicketHistory(
      ticket.tic_pro_id
    );
    setAssetTicketHistory(assetHistory);
  };

  const getAssetDetail = async (assetId: number) => {
    if (!assetId) {
      return;
    }
    const fetchAsset = await assetService.backofficeGetAsset(assetId);
    setAsset(fetchAsset);
  };

  const handlePlayPauseStopwatch = async (type: string) => {
    if (type === 'pause') {
      addHistoryEvent('paused', 'Ticket werd gepauseerd.');
    }
    if (!time) addHistoryEvent('Resumed', 'Ticket werd opnieuw gestart.');

    addHistoryEvent('Started', 'Ticket werd gestart.');

    if (ticket.timeEntries.start_timestamp) {
    } else {
      addTimeEntry(ticket.tic_id, {
        start_timestamp: new Date(),
        emp_id: ticket.assignedEmployee.med_id,
      });
    }
  };

  const searchAsset = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setAssetSearch(e.target.value);
    if (e.target.value.length < 3) {
      return null;
    }
    setAssetLoading(true);
    const results = await assetService.backofficeFindAsset(e.target.value);
    setAssets(results);
    setAssetLoading(false);
  };

  const assignAssetToTicket = async (id: number) => {
    if (!id) {
      return;
    }
    setAssetLoading(true);
    const update = ticketService.updateTicket(ticket.tic_id, {
      tic_pro_id: id,
    });
    setAssetLoading(false);
    setToggleAssetSearch(false);
  };

  const handleAssetToggle = () => {
    setToggleAssetSearch((prevCheck) => !prevCheck);
  };

  useEffect(() => {
    fetchHistory();
    checkEntries();
    getAssetDetail(ticket.tic_pro_id);
  }, [ticket.tic_id]);

  const formatTime = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const pad = (num: number): string => num.toString().padStart(2, '0');

    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  return (
    <div className="flex flex-col items-center justify-center p-4 font-sans antialiased">
      <div className="max-w-3xl w-full rounded-lg p-6">
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between">
            <div className="inline-grid grid-flow-col auto-cols-auto gap-2">
              <div>
                <img
                  src={ticket.assignedEmployee.med_foto}
                  alt={ticket.assignedEmployee.name}
                  className="w-12 h-12 rounded-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src =
                      'https://placehold.co/80x80/aabbcc/ffffff?text=User';
                  }}
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  {ticket.tic_onderwerp}
                  <span className="text-gray-500 text-xl">
                    #{ticket.tic_id}
                  </span>
                </h1>
                <div className="flex gap-1">
                  <TicketTag
                    style={ticket.tic_status}
                    label={ticket.tic_status}
                  />
                  <TicketTag
                    label={ticket.company.bedLevel}
                    style={ticket.company.bedLevel}
                  />
                </div>
              </div>
            </div>
            <div>
              {roundedStoppedTime !== null && (
                <>
                  <p>
                    Total Time:{' '}
                    <span className="font-semibold">
                      {formatTime(roundedStoppedTime)}
                    </span>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        <TicketDetailHeader
          company={ticket.company.bedNaam}
          ticket_id={ticket.tic_id}
          issuer={ticket.gebruiker?.geb_naam}
          created_at={ticket.created_at}
        />

        {asset ? (
          <AssetHeader
            name={asset.ordPro_naam}
            serial={asset.ordSer_serial}
            rmm={asset.device_id}
            invoice={asset.ordPro_invoicenr}
            invoiceDate={asset.ordPro_invoiceDate}
            showHistory={fetchAssetHistory}
          />
        ) : (
          <Cta
            style="small"
            color="default"
            text={toggleAssetSearch ? 'Sluiten' : 'Assign asset'}
            onClick={handleAssetToggle}
          />
        )}

        <div className="my-4">
          {toggleAssetSearch ? (
            <div>
              <InputField
                label="Zoek asset"
                type="text"
                name="search"
                value={assetSearch}
                placeholder="Search asset"
                onChange={searchAsset}
              />
              <SelectAssetTable
                assets={assets}
                onclick={assignAssetToTicket}
                loading={assetLoading}
              />
            </div>
          ) : (
            ''
          )}
        </div>

        <div className="rounded-lg">
          <h2 className="text-xl font-semibold text-gray-700 mb-3 flex items-center">
            Ticket Information
          </h2>
          <div className="space-y-2 text-gray-700 text-sm">
            <p>
              <span className="font-semibold">Subject:</span>{' '}
              {ticket.tic_onderwerp}
            </p>

            <p className="font-semibold mb-1">Details:</p>
            <p className="text-gray-800 ">{ticket.tic_omschrijving}</p>
          </div>
        </div>
        <div className="mt-5"></div>

        {ticket.tic_pro_id ? (
          <Cta
            style="small"
            color="default"
            text="History"
            onClick={() => fetchAssetHistory(ticket.tic_pro_id)}
          />
        ) : (
          ''
        )}

        <div className="mb-4 mt-4">
          <div className="mb-">
            <h2 className="text-xl font-semibold text-gray-700 mb-2 flex items-center">
              Time tracker
            </h2>
            {timeEntries ? (
              <TicketTimeEntries
                entries={timeEntries}
                ticket_id={ticket.tic_id}
                setTimeEntries={setTimeEntries}
              />
            ) : (
              ''
            )}
          </div>

          {/* <TicketHistory history={ticketHistory} /> */}
        </div>
      </div>
    </div>
  );
};

export default function TicketDetailWrapper({
  data,
}: {
  data: { ticket: Ticket };
}) {
  return (
    <BoTicketDetailContainer
      employee={data.ticket.assignedEmployee}
      ticket={data.ticket}
    />
  );
}
